'use client';

import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit, Plus, Search } from 'lucide-react';

interface Brand {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
}

const BrandsPage = () => {
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
    });

    // Load brands
    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/admin/brands');
            const data = await response.json();
            setBrands(data.brands || []);
        } catch (error) {
            console.error('Hiba az márka betöltésénél:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchBrands = async (query: string) => {
        setSearchQuery(query);
        if (query.length === 0) {
            fetchBrands();
            return;
        }

        try {
            const response = await fetch(`/api/admin/brands?q=${encodeURIComponent(query)}`);
            const data = await response.json();
            setBrands(data.brands || []);
        } catch (error) {
            console.error('Hiba a márka keresésénél:', error);
        }
    };

    const handleOpenDialog = () => {
        setFormData({ name: '' });
        setIsEditing(false);
        setEditingId(null);
        setIsOpen(true);
    };

    const handleEditBrand = (brand: Brand) => {
        setFormData({
            name: brand.name,
        });
        setEditingId(brand.id);
        setIsEditing(true);
        setIsOpen(true);
    };

    const handleDeleteBrand = async (id: number) => {
        if (!window.confirm('Biztosan szeretnéd törölni ezt a márkát?')) {
            return;
        }

        setDeleting(true);
        try {
            const response = await fetch(`/api/admin/brands/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setBrands(brands.filter(b => b.id !== id));
            } else {
                alert('Hiba a márka törlésekor');
            }
        } catch (error) {
            console.error('Hiba a márka törlésekor:', error);
            alert('Hiba a márka törlésekor');
        } finally {
            setDeleting(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            alert('A márka neve szükséges!');
            return;
        }

        setSaving(true);
        try {
            const method = isEditing ? 'PATCH' : 'POST';
            const url = isEditing
                ? `/api/admin/brands/${editingId}`
                : '/api/admin/brands';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    brand: {
                        name: formData.name.trim(),
                    },
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (isEditing) {
                    setBrands(brands.map(b => b.id === editingId ? data.brand : b));
                } else {
                    setBrands([...brands, data.brand]);
                }
                setIsOpen(false);
                setFormData({ name: '' });
                setEditingId(null);
            } else {
                const error = await response.json();
                alert(error.errors?.[0] || 'Hiba a márka mentésekor');
            }
        } catch (error) {
            console.error('Hiba a márka mentésekor:', error);
            alert('Hiba a márka mentésekor');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Márkák
                </h1>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <Search
                            size={18}
                            style={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#888',
                            }}
                        />
                        <Input
                            type="text"
                            placeholder="Márka keresése..."
                            value={searchQuery}
                            onChange={(e) => searchBrands(e.target.value)}
                            style={{ paddingLeft: '40px' }}
                        />
                    </div>
                    <Button onClick={handleOpenDialog}>
                        <Plus size={18} style={{ marginRight: '6px' }} />
                        Új márka
                    </Button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Betöltés…
                </div>
            ) : brands.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Nincsenek márkák
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Név</TableHead>
                                <TableHead>Létrehozva</TableHead>
                                <TableHead style={{ textAlign: 'right' }}>Műveletek</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {brands.map((brand) => (
                                <TableRow key={brand.id}>
                                    <TableCell style={{ fontWeight: 500 }}>{brand.name}</TableCell>
                                    <TableCell style={{ color: '#888', fontSize: '13px' }}>
                                        {new Date(brand.created_at).toLocaleDateString('hu-HU')}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleEditBrand(brand)}
                                            >
                                                <Edit size={16} />
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteBrand(brand.id)}
                                                disabled={deleting}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {isEditing ? 'Márka szerkesztése' : 'Új márka'}
                        </DialogTitle>
                        <DialogDescription>
                            {isEditing ? 'Módosítsd a márka adatait' : 'Adj meg új márka adatokat'}
                        </DialogDescription>
                    </DialogHeader>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <Label htmlFor="name">Márka neve *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                placeholder="pl. Coca-Cola"
                                style={{ marginTop: '8px' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outline"
                                onClick={() => setIsOpen(false)}
                                disabled={saving}
                            >
                                Mégse
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Mentés…' : 'Mentés'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BrandsPage;
